// HomePage — redirect-by-role + fallback genérico.
//
// Tras login, todo rol con pantalla propia redirige a su home (vía
// useHomePath, fuente única: getHomeForRole + regla de personal dedicado a
// mantenimiento). El fallback con contexto del JWT + smoke test de /api/me
// solo se ve para roles sin home mapeado.

import { useEffect, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Spinner } from '../../components/ui/spinner';
import { useAuth, useHomePath } from '../../lib/auth';
import { identidadUsuario } from '../../lib/identidad';
import { API_URL, supabase } from '../../lib/supabase';

type FetchState =
  | { kind: 'loading' }
  | { kind: 'ok'; data: unknown }
  | { kind: 'error'; status?: number; message: string };

export function HomePage() {
  const { state, signOut } = useAuth();
  const homePath = useHomePath();
  const [meResponse, setMeResponse] = useState<FetchState>({ kind: 'loading' });

  useEffect(() => {
    if (state.kind !== 'signed_in') return;
    const token = state.session.access_token;
    const controller = new AbortController();

    fetch(`${API_URL}/api/me/`, {
      headers: { Authorization: `Bearer ${token}` },
      signal: controller.signal,
    })
      .then(async (res) => {
        const body = await res.json().catch(() => null);
        if (!res.ok) {
          setMeResponse({
            kind: 'error',
            status: res.status,
            message: JSON.stringify(body) || `HTTP ${res.status}`,
          });
          return;
        }
        setMeResponse({ kind: 'ok', data: body });
      })
      .catch((err: unknown) => {
        if (err instanceof Error && err.name === 'AbortError') return;
        setMeResponse({
          kind: 'error',
          message: err instanceof Error ? err.message : String(err),
        });
      });

    return () => controller.abort();
  }, [state]);

  if (state.kind !== 'signed_in') {
    return null;
  }

  // Routing por rol — todo rol con home propio redirige (fuente única:
  // useHomePath = getHomeForRole + regla de dedicado a mantenimiento). Para
  // rol líder hay que esperar /api/me antes de decidir: un dedicado NO debe
  // aterrizar ni un instante en /capturar.
  if (homePath.resolviendo) {
    return (
      <main className="flex min-h-screen flex-col items-center justify-center gap-3 bg-surface-base p-6 text-fg">
        <Spinner className="size-6" />
        <p className="text-sm text-fg-secondary">Preparando su espacio de trabajo…</p>
      </main>
    );
  }
  if (homePath.home !== '/') return <Navigate to={homePath.home} replace />;

  const { claims } = state;
  const hasMultipleTenants = claims.tenants_disponibles.length > 1;
  const activeTenant = claims.tenants_disponibles.find((t) => t.id === claims.tenant_id_activo);

  return (
    <main className="mx-auto flex min-h-screen max-w-2xl flex-col gap-6 bg-surface-base p-6 text-fg">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="font-serif text-2xl tracking-tight text-fg">
            Jidokap<span className="text-dot">.</span>
          </h1>
          <p className="text-xs text-fg-secondary">
            Bienvenido,{' '}
            <strong className="text-fg">{identidadUsuario(claims.email).etiqueta}</strong>
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={() => signOut()}>
          Cerrar sesión
        </Button>
      </header>

      <Card>
        <CardContent className="p-4 pt-4">
          <div className="mb-3 flex items-center justify-between">
            <h2 className="text-[10px] font-medium uppercase tracking-[0.1em] text-fg-tertiary">
              Organización activa
            </h2>
            {hasMultipleTenants ? (
              <Link
                to="/elegir-tenant"
                className="text-xs text-fg-secondary underline hover:text-fg"
              >
                Cambiar organización
              </Link>
            ) : null}
          </div>
          <p className="text-base font-medium text-fg">{activeTenant?.nombre ?? '—'}</p>
          <p className="mt-1 text-xs text-fg-tertiary">
            Operando como <code className="font-mono">{claims.rol}</code>
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-4 pt-4">
          <h2 className="mb-3 text-[10px] font-medium uppercase tracking-[0.1em] text-fg-tertiary">
            Contexto (claims del JWT)
          </h2>
          <dl className="grid grid-cols-[max-content_1fr] gap-x-4 gap-y-1 text-sm">
            <dt className="text-fg-tertiary">user_id</dt>
            <dd className="font-mono text-xs text-fg">{claims.sub}</dd>
            <dt className="text-fg-tertiary">tenant_id_activo</dt>
            <dd className="font-mono text-xs text-fg">{claims.tenant_id_activo}</dd>
            <dt className="text-fg-tertiary">rol</dt>
            <dd className="font-mono text-xs text-fg">{claims.rol}</dd>
            <dt className="text-fg-tertiary">tenants disponibles</dt>
            <dd className="font-mono text-xs text-fg">{claims.tenants_disponibles.length}</dd>
          </dl>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-4 pt-4">
          <h2 className="mb-3 text-[10px] font-medium uppercase tracking-[0.1em] text-fg-tertiary">
            Smoke test: GET /api/me
          </h2>
          <MeResult state={meResponse} />
        </CardContent>
      </Card>

      <footer className="text-center text-[11px] text-fg-tertiary">
        Home genérico para roles sin pantalla propia. Roles operativos redirigen automáticamente.
      </footer>
    </main>
  );
}

function MeResult({ state }: { state: FetchState }) {
  if (state.kind === 'loading') {
    return (
      <p className="flex items-center gap-2 text-sm text-fg-secondary">
        <span className="size-2 animate-pulse rounded-full bg-fg-tertiary" />
        Consultando…
      </p>
    );
  }
  if (state.kind === 'error') {
    return (
      <div className="space-y-2 text-sm">
        <p className="font-medium text-crit">Error{state.status ? ` (${state.status})` : ''}</p>
        <pre className="overflow-auto rounded bg-crit-soft p-3 text-xs text-crit">
          {state.message}
        </pre>
      </div>
    );
  }
  return (
    <pre className="overflow-auto rounded bg-surface-overlay p-3 text-xs text-fg">
      {JSON.stringify(state.data, null, 2)}
    </pre>
  );
}

void supabase;
